// Module to contain react hooks

import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
  useFirebase,
  normalizeTeamDoc,
  normalizeTournamentDoc,
  normalizeUserDoc,
} from '../components/FirebaseContextProvider';
import { useTrail } from '../components/TrailProvider';
import { getBaseRoute } from '../components/LoginCard';
import { getTeamDivision, toOptions } from './misc';
import { gameDivision, hasRates, sumPaid } from './gameReport';
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import {
  db,
  waiverGetUnsigned,
  tournamentRefereeRateSet,
  tournamentRefereeGameRateSet,
} from './firebase';
import { defaultTimeZone } from './consts';
import { PaymentPurposes, RefereeStatuses, UsersRoles } from './enums';

// Trail-aware back-button handler for card templates
export function useBackBtn() {
  const navigate = useNavigate();
  const { trail } = useTrail();
  const { user } = useFirebase();

  return () => {
    if (trail.length > 1) {
      // we have a previous entry in this tab
      navigate(-1);
    } else {
      const base = getBaseRoute(user);
      navigate(base, { replace: true }); // replace login/blank with base
    }
  };
}

// Keep the named subcollections of one tournament subscribed for as long as
// this component is mounted, and report whether they have all landed. The
// provider counts holders, so several components may ask for the same part and
// none of them has to know about the others.
//
// The registration is keyed on a sorted joined string rather than on the array
// itself: a `['games', 'fields']` literal is a new object on every render and
// would re-open the listeners each time. `'new'` is skipped — the tournament
// creation route renders the card with an id that has no document behind it.
export function useTournamentParts(tournamentId, parts) {
  const { acquireTournamentParts, tournamentsById } = useFirebase();

  const partsKey = [...parts].sort().join('|');
  const id = tournamentId === 'new' ? null : tournamentId;

  useEffect(() => {
    if (!id || !partsKey) return undefined;
    return acquireTournamentParts(id, partsKey.split('|'));
  }, [id, partsKey, acquireTournamentParts]);

  const tournament = tournamentsById.get(id);

  if (!partsKey) return false;
  return partsKey
    .split('|')
    .every((part) => tournament?.[`${part}Loaded`] === true);
}

const entityByIdKey = {
  users: 'usersById',
  teams: 'teamsById',
  tournaments: 'tournamentsById',
};
const entityStatusKey = {
  users: 'userStatusById',
  teams: 'teamStatusById',
  tournaments: 'tournamentStatusById',
};

// Keep a set of by-id documents subscribed for as long as this component is
// mounted (via the provider's entity registry), and read back whatever has
// resolved so far from usersById/teamsById/tournamentsById. Keyed on a sorted
// joined string, not on `ids` itself: an inline `[id]` array is a new object
// every render and must not reopen the underlying chunk listeners.
export function useEntities(kind, ids) {
  const firebase = useFirebase();
  const { acquireEntities } = firebase;
  const byId = firebase[entityByIdKey[kind]];
  const statusById = firebase[entityStatusKey[kind]];

  const idsKey = [...new Set((ids || []).filter((id) => id && id !== 'new'))]
    .sort()
    .join('|');

  useEffect(() => {
    if (!idsKey) return undefined;
    return acquireEntities(kind, idsKey.split('|'));
  }, [kind, idsKey, acquireEntities]);

  return useMemo(() => {
    const wanted = idsKey ? idsKey.split('|') : [];
    return {
      entities: wanted.map((id) => byId.get(id) ?? null),
      isResolved: wanted.every(
        (id) => byId.has(id) || statusById.get(id) === 'missing',
      ),
    };
  }, [idsKey, byId, statusById]);
}

export function useUser(id) {
  const { entities, isResolved } = useEntities(
    'users',
    id && id !== 'new' ? [id] : [],
  );
  return { entity: entities[0] ?? null, isResolved };
}

export function useTeam(id) {
  const { entities, isResolved } = useEntities(
    'teams',
    id && id !== 'new' ? [id] : [],
  );
  return { entity: entities[0] ?? null, isResolved };
}

export function useTournament(id) {
  const { entities, isResolved } = useEntities(
    'tournaments',
    id && id !== 'new' ? [id] : [],
  );
  return { entity: entities[0] ?? null, isResolved };
}

// Game referee picker options: the tournament's accepted roster only. A
// referee assigned earlier and not accepted now stays listed, marked, so the
// picker shows them and they can be taken off. Pass a null id to stand down
export function useRefereeOptions(tournamentId, assigned) {
  const { tournamentsById, refereesById } = useFirebase();
  useTournamentParts(tournamentId, ['referees']);
  const roster = tournamentId
    ? tournamentsById.get(tournamentId)?.referees
    : undefined;
  const acceptedIds = useMemo(
    () =>
      (roster || [])
        .filter((entry) => entry.status === RefereeStatuses.ACCEPTED)
        .map((entry) => entry.id),
    [roster],
  );
  const { entities } = useEntities('users', acceptedIds);

  return useMemo(() => {
    const accepted = new Set(acceptedIds);
    const offRoster = (assigned || [])
      .filter((id) => id && !accepted.has(id))
      .map((id) => ({
        id,
        name: `${refereesById.get(id)?.name ?? 'Unknown'} (not on roster)`,
      }));
    return [...toOptions(entities.filter(Boolean)), ...toOptions(offRoster)];
  }, [acceptedIds, entities, assigned, refereesById]);
}

// Get an array of all the tournament's divisions: its admitted teams', then
// the ones its games name, which is all a teamless event's TBD games carry
export function useDivisions(tournamentId) {
  const { tournamentsById, teamsById } = useFirebase();

  useTournamentParts(tournamentId, ['teams', 'games']);

  const tournament = useMemo(
    () => tournamentsById.get(tournamentId),
    [tournamentsById, tournamentId],
  );

  return useMemo(() => {
    if (!tournament?.teams) return [];

    const divSet = new Set();
    for (const { id, admitted } of tournament?.teams) {
      if (id == null || !admitted) continue;
      const team = teamsById.get(id);
      if (team) divSet.add(getTeamDivision(team));
    }
    // After the teams', so a game's division never shifts a team's color
    for (const game of tournament.games ?? []) {
      const division = gameDivision(game, teamsById);
      if (division) divSet.add(division);
    }

    return Array.from(divSet);
  }, [tournament?.teams, tournament?.games, teamsById]);
}

// Team IDs the current user is associated with: their own teams for a
// coach/player, or the union of their approved children's teams for a parent.
// Computed in the provider, which also keeps the children's documents
// registered — the parent branch is the one thing here that is not on the
// caller's own document. Kept as a hook so its four call sites did not move.
export function useUserTeamIds() {
  return useFirebase().myTeamIds;
}

// One live query, open while the caller is mounted. `buildQuery` returns a
// query, or null for nothing to read (a real, empty answer); it is the reset
// key, as are `map` and `empty`, so keep all three stable. `ready: false`
// holds the read back. A refusal loads but never syncs: it proves no absence.
export function useLiveQuery(
  buildQuery,
  { ready = true, map, empty, descriptor },
) {
  const { user, reportListenerError } = useFirebase();
  const callerUid = user?.id;
  const isReady = !!ready;

  const errorInfo = useRef(descriptor);
  errorInfo.current = descriptor;

  const [state, setState] = useState({ source: null });

  useEffect(() => {
    const base = { source: buildQuery, isReady, data: empty, isFailed: false };
    if (!isReady) {
      setState({ ...base, isLoaded: false, isSynced: false });
      return undefined;
    }
    const target = buildQuery();
    if (!target) {
      setState({ ...base, isLoaded: true, isSynced: true });
      return undefined;
    }
    setState({ ...base, isLoaded: false, isSynced: false });

    let published = false;

    return onSnapshot(
      target,
      // Without it the cache → server flip is silent and nothing ever syncs
      { includeMetadataChanges: true },
      (snapshot) => {
        const isMetaOnly = published && snapshot.docChanges().length === 0;
        published = true;
        const data = isMetaOnly ? null : map(snapshot);

        setState((prev) => {
          const isSynced = prev.isSynced || !snapshot.metadata.fromCache;
          if (data) return { ...base, data, isLoaded: true, isSynced };
          // A metadata event repeats published data: keep its identity
          return prev.isLoaded && prev.isSynced === isSynced
            ? prev
            : { ...prev, isLoaded: true, isSynced };
        });
      },
      (err) => {
        reportListenerError(err, callerUid, {
          operation: 'listen',
          ...errorInfo.current,
        });
        setState({ ...base, isLoaded: true, isSynced: false, isFailed: true });
      },
    );
  }, [isReady, buildQuery, map, empty, callerUid, reportListenerError]);

  // Until the effect catches up, the state still answers the previous query
  const isCurrent = state.source === buildQuery && state.isReady === isReady;
  return {
    data: isCurrent ? state.data : empty,
    isLoaded: isCurrent && state.isLoaded,
    isSynced: isCurrent && state.isSynced,
    isFailed: isCurrent && state.isFailed,
  };
}

// The two shapes `users` allow-list can prove (backend firestore.rules:46-55
// and 68-80): the caller's active organization, or their own uid on the
// documents of people on the teams they coach. A query holds one of them.
// No roles at all means "no query" — the caller does not need the list.
function usersScope({ roles, activeOrg, coachUid }) {
  if (!roles.length) return null;
  // `in` is a disjunction of equalities: it narrows without touching the
  // proving clause below, and needs no index of its own
  const byRole =
    roles.length === 1
      ? where('role', '==', roles[0])
      : where('role', 'in', roles);

  if (coachUid) return [byRole, where('coachIds', 'array-contains', coachUid)];
  if (!activeOrg) return null;
  return [byRole, where('organizations', 'array-contains', activeOrg)];
}

// One `users` list, open only while a screen that genuinely needs it is
// mounted (P3). `isLoaded` tells "still reading" apart from "nobody matches" —
// every consumer draws its empty state from the absence of documents.
// `role` is one role or several.
function useUsersList({ role, coachUid }) {
  const { activeOrg, isUserResolved, isOrgConfirmed, isOrgSwitching } =
    useFirebase();

  // Keyed on the joined roles, never on the array: a `[PLAYER, COACH]` literal
  // is a new object every render and would reopen the listener each time (P7)
  const roleKey = (
    Array.isArray(role) ? [...role].sort() : role ? [role] : []
  ).join('|');

  // No roles, or confirmed to have no organization — a real, empty answer
  const buildQuery = useCallback(() => {
    const roles = roleKey ? roleKey.split('|') : [];
    const scope = usersScope({ roles, activeOrg, coachUid });
    return scope && query(collection(db, 'users'), ...scope);
  }, [roleKey, activeOrg, coachUid]);

  const { data, isLoaded, isSynced } = useLiveQuery(buildQuery, {
    // The coach query carries no organization and waits on the caller's own
    // document alone; the org one also needs the server's word on it (P8)
    ready:
      !roleKey ||
      (isUserResolved && (!!coachUid || (isOrgConfirmed && !isOrgSwitching))),
    map: readUsers,
    empty: noUsers,
    descriptor: {
      path: 'users',
      filters: [
        `role in [${roleKey}]`,
        coachUid
          ? `coachIds array-contains ${coachUid}`
          : `organizations array-contains ${activeOrg}`,
      ],
      orgScoped: !coachUid,
      message: 'Failed to load users',
      toastId: 'org-users',
    },
  });

  return { users: data, isLoaded, isSynced };
}

const noUsers = [];
const readUsers = (snapshot) =>
  snapshot.docs.map((d) => normalizeUserDoc(d.id, d.data()));

// Everyone in the organization with a given role, or with any of several —
// the admin lists, the coach and referee pickers, tournament check-in. Pass
// `null` to hold the query back: these consumers sit inside cards that render
// for every role, and only an admin may open the picker.
export function useOrgUsers(role) {
  return useUsersList({ role: role ?? null, coachUid: null });
}

// A team's players. The team's coach proves the query with their own uid and
// reads only the teams they coach; an admin, a player or a parent on the same
// tab still reads the organization — nothing keys `users` to them.
export function useTeamRoster(teamId) {
  const { user, userIsCoach } = useFirebase();
  const isTeamCoach = !!teamId && userIsCoach && !!user?.teams?.includes(teamId);
  const { users, isLoaded, isSynced } = useUsersList({
    role: teamId ? UsersRoles.PLAYER : null,
    coachUid: isTeamCoach ? user.id : null,
  });
  // Either branch is wider than one team: the coach's covers every team they
  // coach, the organization's the whole role
  const players = useMemo(
    () => (teamId ? users.filter((u) => u.teams?.includes(teamId)) : []),
    [users, teamId],
  );
  return { players, isLoaded, isSynced };
}

// The organization's teams as rows rather than as documents — the admin lists,
// the pickers, their filters and their search. The backend keeps them in at
// most eight shards under organizations/{org}/directory, so this is six reads
// where the org-wide `teams` subscription was five hundred. A row carries only
// what a list renders; anything that needs the team itself reads it by id
// through the entity registry (see `useTeam`), and `coachesByTeamId` is never
// built from here — a visitor cannot read the directory at all.
//
// `enabled` holds the query back the way `useOrgUsers(null)` does: these
// consumers sit inside cards that render for every role.
export function useTeamsDirectory(enabled = true) {
  const { user, activeOrg, isOrgConfirmed, isOrgSwitching } = useFirebase();

  // Disabled, or confirmed to have no organization — a real, empty answer
  const buildQuery = useCallback(
    () =>
      enabled && activeOrg
        ? collection(db, 'organizations', activeOrg, 'directory')
        : null,
    [enabled, activeOrg],
  );

  const { data, isLoaded, isSynced } = useLiveQuery(buildQuery, {
    // The path names the organization, so a cached guess about it reads
    // somebody else's directory — or none (P8)
    ready: !enabled || (!!user?.id && isOrgConfirmed && !isOrgSwitching),
    map: readDirectory,
    empty: noTeams,
    descriptor: {
      path: `organizations/${activeOrg}/directory`,
      orgScoped: true,
      message: 'Failed to load teams',
      toastId: 'teams-directory',
    },
  });

  // The lists want rows; the four callers that resolve a name by id want a map
  // and would otherwise scan (P5)
  const teamsById = useMemo(() => new Map(data.map((t) => [t.id, t])), [data]);

  return { teams: data, isLoaded, isSynced, teamsById };
}

const noTeams = [];

// `kind` is what lets an old client skip a shard it does not know —
// players-{seq} or tournaments-{seq} may appear beside these one day.
// `seq` only makes the concatenation deterministic; the order of the
// rows themselves carries no meaning and every consumer sorts.
const readDirectory = (snapshot) =>
  snapshot.docs
    .filter((d) => d.get('kind') === 'teams')
    .sort((a, b) => (a.get('seq') ?? 0) - (b.get('seq') ?? 0))
    .flatMap((d) => d.get('items') ?? [])
    .map(normalizeTeamDoc);

// The current user's teams that take part in a given tournament. A team is
// included when the tournament division matches (sport/gender/age) and the team
// has been invited by an admin; for players/parents it must additionally be
// admitted, while coaches see every invited team (so they can register/pay).
//
// Returns { isReady, teams }. `invited`/`admitted` live in the
// tournaments/{id}/teams subcollection, which arrives after the tournament doc
// and is refilled whenever the tournaments snapshot reports any change. Since
// the test is an allow-list, unloaded data reads as "invited to nothing" —
// indistinguishable from a real empty result, so callers are handed the flag
// rather than a wrong answer. `isReady` therefore covers every input the answer
// depends on, not just the subcollection.
export function useUserTournamentTeams(tournamentId) {
  const {
    tournamentsById,
    teamsById,
    userIsPlayer,
    userIsParent,
    isTeamsLoaded,
    isMyTeamIdsResolved,
  } = useFirebase();
  const userTeamIds = useUserTeamIds();

  useTournamentParts(tournamentId, ['teams']);

  const tournament = useMemo(
    () => tournamentsById.get(tournamentId),
    [tournamentsById, tournamentId],
  );

  // Both halves of the match have to be in: the subcollection allow-list, and
  // the team documents it is matched against. They arrive on independent
  // listeners and the small subcollection usually wins, so gating on
  // `teamsLoaded` alone reports "ready, no teams" for a moment — long enough
  // for a caller to act on the wrong answer. `isMyTeamIdsResolved` covers the
  // other half — for a parent that is a three-step chain, links → children →
  // their teams.
  const isReady =
    tournament?.teamsLoaded === true &&
    isMyTeamIdsResolved &&
    isTeamsLoaded;

  const teams = useMemo(() => {
    if (!tournament || !isReady) return [];
    const admittedOnly = userIsPlayer || userIsParent;

    return userTeamIds
      .map((teamId) => teamsById.get(teamId))
      .filter((team) => {
        if (!team) return false;
        // division match
        if (
          !tournament.sports.includes(team.sport) ||
          !tournament.genders.includes(team.gender) ||
          !tournament.ages.includes(team.age)
        )
          return false;
        const entry = tournament.teams.find((t) => t.id === team.id);
        if (entry?.invited !== true) return false;
        if (admittedOnly && !entry?.admitted) return false;
        return true;
      });
  }, [tournament, isReady, userTeamIds, teamsById, userIsPlayer, userIsParent]);

  return { isReady, teams };
}

// "Which tournaments is this person involved in", without opening a listener on
// every tournament's subcollections to find out. Both queries go across the
// collection group, so the tournament id comes off the document's grandparent
// path; both are covered by a recursive-wildcard rule and a COLLECTION_GROUP
// index (see firestore.rules and firestore.indexes.json in the backend repo).
//
// `isLoaded` is what tells "still reading" apart from "involved in nothing" —
// an allow-list built from an unfinished query reads as an empty answer.
function useParentTournamentIds(kind, constraint, userId, message) {
  const buildQuery = useCallback(
    () =>
      userId ? query(collectionGroup(db, kind), constraint(userId)) : null,
    [kind, constraint, userId],
  );

  const { data, isLoaded } = useLiveQuery(buildQuery, {
    map: readParentIds,
    empty: noIds,
    descriptor: { path: `collectionGroup(${kind})`, message },
  });

  return { ids: data, isLoaded };
}

const noIds = new Set();
const readParentIds = (snapshot) =>
  new Set(snapshot.docs.map((d) => d.ref.parent.parent.id));

const refereeGames = (userId) => where('referees', 'array-contains', userId);
const managedTournaments = (userId) => where('managerId', '==', userId);

// Tournaments where the given user is assigned to at least one game
export function useRefereeTournamentIds(refereeId) {
  return useParentTournamentIds(
    'games',
    refereeGames,
    refereeId,
    'Failed to load the referee assignments',
  );
}

// The games the given user referees, by tournament id: the same query as
// above, kept whole for a screen that prices them
export function useRefereeGames(refereeId) {
  const buildQuery = useCallback(
    () =>
      refereeId
        ? query(collectionGroup(db, 'games'), refereeGames(refereeId))
        : null,
    [refereeId],
  );

  const { data, isLoaded } = useLiveQuery(buildQuery, {
    map: readGamesByTournament,
    empty: noGamesByTournament,
    descriptor: {
      path: 'collectionGroup(games)',
      message: 'Failed to load the referee assignments',
    },
  });

  return { gamesByTournament: data, isLoaded };
}

const noGamesByTournament = new Map();
const readGamesByTournament = (snapshot) => {
  const byTournament = new Map();
  for (const d of snapshot.docs) {
    const tournamentId = d.ref.parent.parent.id;
    if (!byTournament.has(tournamentId)) byTournament.set(tournamentId, []);
    byTournament.get(tournamentId).push({ id: d.id, ...d.data() });
  }
  return byTournament;
};

// Tournaments where the given user is a field manager
export function useManagerTournamentIds(managerId) {
  return useParentTournamentIds(
    'managers',
    managedTournaments,
    managerId,
    'Failed to load the managed tournaments',
  );
}

// The signed-in referee's own roster entries across tournaments. Nobody else
// can run this query: the rules prove it through the refereeId filter, so an
// admin on a referee's card reads nothing here
export function useMyRefereeRoster() {
  const { user, userIsReferee } = useFirebase();
  const uid = userIsReferee ? user?.id : null;

  const buildQuery = useCallback(
    () =>
      uid
        ? query(collectionGroup(db, 'referees'), where('refereeId', '==', uid))
        : null,
    [uid],
  );

  const { data, isLoaded, isSynced } = useLiveQuery(buildQuery, {
    map: readRosterEntries,
    empty: noRosterEntries,
    descriptor: {
      path: 'collectionGroup(referees)',
      message: 'Failed to load your referee invitations',
    },
  });

  return { entries: data, isLoaded, isSynced };
}

// A tournament's accepted referees, for one of them: the rules open the
// roster to an accepted referee only through the status filter
export function useAcceptedReferees(tournamentId, enabled) {
  const buildQuery = useCallback(
    () =>
      enabled && tournamentId
        ? query(
            collection(db, 'tournaments', tournamentId, 'referees'),
            where('status', '==', RefereeStatuses.ACCEPTED),
          )
        : null,
    [tournamentId, enabled],
  );

  const { data, isLoaded } = useLiveQuery(buildQuery, {
    map: readRosterEntries,
    empty: noRosterEntries,
    descriptor: {
      path: `tournaments/${tournamentId}/referees`,
      filters: ['status == accepted'],
      message: 'Failed to load the referee roster',
    },
  });

  return { referees: data, isLoaded };
}

const noRosterEntries = [];
const readRosterEntries = (snapshot) =>
  snapshot.docs.map((d) => ({
    id: d.id,
    tournamentId: d.ref.parent.parent.id,
    ...d.data(),
  }));

// Tournaments relevant to the current user's Live tab.
// Returns { isReady, entries }; entry = { tournament, myTeamIds, isRefereeMode, isLive }.
// A tournament is relevant when its schedule is published, it hasn't ended,
// and the user has an invited and admitted team in it (coach/player/parent via
// approved children) or is a referee with assigned games. Live tournaments
// (running today) come first, then upcoming by start date.
export function useLiveTournaments() {
  const {
    user,
    tournaments,
    userIsCoach,
    userIsPlayer,
    userIsParent,
    userIsReferee,
    isTournamentsLoaded,
    isMyTeamIdsResolved,
  } = useFirebase();

  // A referee's tournaments used to be found by scanning the games of every
  // tournament, which is what kept all of them subscribed. The query answers
  // the same question directly and is only sent for a referee.
  const { ids: refereedTournIds, isLoaded: areRefereedTournsLoaded } =
    useRefereeTournamentIds(userIsReferee ? user?.id : null);

  // `isMyTeamIdsResolved` covers who this user plays for — for a parent that
  // is the links, the children's documents and their teams; a referee has no
  // teams at all and waits on the query above instead. Deliberately not
  // `isTeamsLoaded`: nothing here reads a team document, only ids, and that
  // flag now follows the tournaments somebody has open — the Live tab opening
  // one would drop it, empty this list, unmount the tab and raise it again.
  const isReady =
    isMyTeamIdsResolved &&
    isTournamentsLoaded &&
    (!userIsReferee || areRefereedTournsLoaded);

  const userTeamIds = useUserTeamIds();

  const entries = useMemo(() => {
    if (!isReady || !user) return [];
    if (!userIsCoach && !userIsPlayer && !userIsParent && !userIsReferee)
      return [];

    const result = [];
    for (const tournament of tournaments) {
      if (!tournament.isSchedulePublished) continue;

      // The provider builds startDate/endDate moments in the browser's guessed
      // timezone — compare calendar-date strings in the tournament tz instead
      const tz = tournament.timezone || defaultTimeZone;
      const today = moment.tz(tz).format('YYYY-MM-DD');
      const startStr = tournament.startDate.format('YYYY-MM-DD');
      const endStr = tournament.endDate.format('YYYY-MM-DD');
      if (endStr < today) continue;

      let myTeamIds = [];
      let isRefereeMode = false;
      if (userIsReferee) {
        isRefereeMode = refereedTournIds.has(tournament.id);
        if (!isRefereeMode) continue;
      } else {
        // The backend keeps `admittedTeamIds` gated on `invited === true`, so
        // it is exactly the old `t.admitted && t.invited === true` pair
        myTeamIds = (tournament.admittedTeamIds || []).filter((id) =>
          userTeamIds.includes(id),
        );
        if (!myTeamIds.length) continue;
      }

      result.push({
        tournament,
        myTeamIds,
        isRefereeMode,
        isLive: startStr <= today && today <= endStr,
      });
    }

    return result.sort((a, b) => {
      if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
      return (
        a.tournament.startDate.valueOf() - b.tournament.startDate.valueOf()
      );
    });
  }, [
    isReady,
    user,
    userIsCoach,
    userIsPlayer,
    userIsParent,
    userIsReferee,
    tournaments,
    userTeamIds,
    refereedTournIds,
  ]);

  return { isReady, entries };
}

function buildWaiverMap(playersRecord) {
  const map = new Map();
  for (const [pid, waivers] of Object.entries(playersRecord)) {
    map.set(pid, waivers.liability === true);
  }
  return map;
}

// Returns { waiverMap: Map<playerId, boolean> } where true = liability signed
// Note: hook destructures params into primitives for deps — object identity is irrelevant
export function useWaiverStatuses({ teamId, parentId, playerId }) {
  const [waiverMap, setWaiverMap] = useState(() => new Map());

  useEffect(() => {
    const payload = teamId
      ? { teamId }
      : parentId
        ? { parentId }
        : playerId
          ? { playerId }
          : null;
    if (!payload) return;

    let cancelled = false;

    async function fetchWaivers() {
      try {
        const { data } = await waiverGetUnsigned(payload);
        if (cancelled) return;
        setWaiverMap(buildWaiverMap(data?.players ?? {}));
      } catch {}
    }

    fetchWaivers();

    return () => {
      cancelled = true;
    };
  }, [teamId, parentId, playerId]);

  return { waiverMap };
}

// Batch version: fires parallel calls for multiple team IDs
export function useWaiverStatusesBatch(teamIds) {
  const [waiverMap, setWaiverMap] = useState(() => new Map());

  const stableKey = useMemo(
    () => (teamIds?.length ? [...teamIds].sort().join(',') : ''),
    [teamIds],
  );

  useEffect(() => {
    if (!stableKey) return;

    let cancelled = false;
    const ids = stableKey.split(',');

    async function fetchAll() {
      try {
        const results = await Promise.all(
          ids.map((id) => waiverGetUnsigned({ teamId: id })),
        );
        if (cancelled) return;
        const map = new Map();
        results.forEach(({ data }) => {
          for (const [k, v] of buildWaiverMap(data?.players ?? {})) {
            map.set(k, v);
          }
        });
        setWaiverMap(map);
      } catch {}
    }

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [stableKey]);

  return { waiverMap };
}

// Frozen at module level: a fresh `[]` per render would defeat every memo
// below and hand each consumer a new array identity on every frame
const emptyTournament = {};
const noGames = [];
const noFields = [];

// Shared data for the tournament game reports (Games and Referees tabs and
// their PDF twins): the tournament itself, its days, games sorted by start
// time, fields in display order and the referees assigned to any game
export function useGameReport(tournamentId) {
  const { tournamentsById, teamsById, refereesById } = useFirebase();

  useTournamentParts(tournamentId, ['games', 'fields']);

  const tournament = tournamentsById.get(tournamentId);
  // Defaults are module constants: fresh literals would defeat the memos
  // below, and returning early is no option — they are hooks
  const {
    startDate,
    endDate,
    games = noGames,
    fields = noFields,
  } = tournament ?? emptyTournament;

  const days = useMemo(() => {
    if (!startDate || !endDate) return [];

    const days = [];
    const day = startDate.clone();
    const lastDay = endDate.clone();

    do {
      days.push(day.clone());
      day.add(1, 'day');
    } while (day.isSameOrBefore(lastDay));

    return days;
  }, [startDate, endDate]);

  const sortedGames = useMemo(
    () =>
      [...games].sort((a, b) =>
        moment(a.start, 'HH:mm').diff(moment(b.start, 'HH:mm')),
      ),
    [games],
  );

  const sortedFields = useMemo(
    () => [...fields].sort((a, b) => a.sortIndex - b.sortIndex),
    [fields],
  );

  const gamesReferees = useMemo(
    () => [...new Set(games.map((game) => game.referees || []).flat())],
    [games],
  );

  // Decided once for the whole pay report: its tables show the Division
  // column together or not at all
  const hasRefereedDivision = useMemo(
    () =>
      games.some(
        (game) => game.referees?.length > 0 && gameDivision(game, teamsById),
      ),
    [games, teamsById],
  );

  const getRefereeName = (id) => refereesById.get(id)?.name || '';

  return {
    tournament,
    days,
    sortedGames,
    sortedFields,
    gamesReferees,
    hasRefereedDivision,
    getRefereeName,
  };
}

// Two `{ rate, exceptions }` that price every game the same way. A referee
// nobody has priced is written as `null` while a clear is in flight, and read
// back from the snapshot as `undefined` once the document is gone — both are
// "no rates", so they are compared through `hasRates` rather than by identity
const sameRates = (a, b) => {
  if (!hasRates(a) || !hasRates(b)) return !hasRates(a) && !hasRates(b);
  if (a.rate !== b.rate) return false;

  const aExceptions = a.exceptions || {};
  const bExceptions = b.exceptions || {};
  const gameIds = Object.keys(aExceptions);

  return (
    gameIds.length === Object.keys(bExceptions).length &&
    gameIds.every((gameId) => aExceptions[gameId] === bExceptions[gameId])
  );
};

const noRates = [];

// Referee pay for one tournament, and the writes that change it. Each caller
// keeps its own copy of the amounts it has written, so the Referees tab hands
// its result to the PDF twin rather than the twin calling this again.
export function useRefereeRates(tournamentId) {
  const { tournamentsById } = useFirebase();
  // Amounts already entered but not yet read back. The writes go through Cloud
  // Functions, and every cell inheriting a rate would otherwise keep showing
  // the old one until the snapshot lands. Keyed by referee, `null` for a
  // referee whose rates are being cleared — absent from the map is a different
  // answer, it means "ask the snapshot"
  const [pending, setPending] = useState(new Map());
  // The tournament `pending` belongs to: a switch drops the copy in the same
  // render, before another tournament's referees could read it
  const [pendingTournId, setPendingTournId] = useState(tournamentId);
  // How many writes are still in flight per tournament and referee, so the last
  // one to land is the one that records what the snapshot said at that moment
  const writing = useRef(new Map());
  // What the snapshot said about a referee when their last write finished.
  // Absent while a write is in flight
  const settled = useRef(new Map());

  useTournamentParts(tournamentId, ['refereeRates']);

  const tournament = tournamentsById.get(tournamentId);
  const docs = tournament?.refereeRates ?? noRates;
  // A refusal never syncs, so the inputs stay disabled rather than offer an
  // empty field over a rate that may exist
  const isRatesSynced = tournament?.refereeRatesSynced === true;

  // Both stored fields are optional: `rate` is absent when a per-game
  // exception was set before the default one, `exceptions` until the first
  const ratesById = useMemo(
    () =>
      new Map(
        docs.map((r) => [
          r.id,
          { rate: r.rate, exceptions: r.exceptions || {} },
        ]),
      ),
    [docs],
  );

  // The rates of the last snapshot, for the handlers: those close over the
  // render that created them, and the map may have moved on since
  const latestById = useRef(ratesById);

  if (pendingTournId !== tournamentId) {
    setPendingTournId(tournamentId);
    setPending(new Map());
  }

  // A write still in flight from the tournament left behind records nothing
  const tournIdRef = useRef(tournamentId);
  tournIdRef.current = tournamentId;
  useEffect(() => {
    settled.current = new Map();
  }, [tournamentId]);

  // The local copy is dropped on evidence, never on the mere absence of a write
  // in flight: the callable answers over one channel and the snapshot arrives
  // over another, so a snapshot read after the write finished may still be the
  // one from before it. Evidence is the snapshot agreeing with the copy, or
  // saying something new about that referee since the write finished — the
  // latter is how the backend wins, sweeping exceptions of games the referee no
  // longer has, which a copy kept for disagreeing would mask for good.
  useEffect(() => {
    latestById.current = ratesById;

    // Collected rather than dropped in place: the updater below may be called
    // more than once for one commit, and reading `settled` it is about to
    // change would answer differently the second time
    let answered = [];

    setPending((prev) => {
      if (prev.size === 0) return prev;

      answered = [...prev]
        .filter(([refereeId, rates]) => {
          const current = ratesById.get(refereeId);
          return (
            sameRates(rates, current) ||
            (settled.current.has(refereeId) &&
              !sameRates(settled.current.get(refereeId), current))
          );
        })
        .map(([refereeId]) => refereeId);

      if (answered.length === 0) return prev;
      const next = new Map(prev);
      answered.forEach((refereeId) => next.delete(refereeId));
      return next;
    });

    answered.forEach((refereeId) => settled.current.delete(refereeId));
  }, [ratesById]);

  const getRates = useCallback(
    (refereeId) => {
      if (pending.has(refereeId)) return pending.get(refereeId) ?? undefined;
      return ratesById.get(refereeId);
    },
    [pending, ratesById],
  );

  // Show the amount at once and let the write follow. A failure means the
  // snapshot never moved, so the rollback goes back to what this edit replaced
  // rather than to the snapshot: an earlier edit of the same referee may be
  // waiting there, already saved
  const apply = async (refereeId, nextRates, write) => {
    let replaced;
    const writeKey = `${tournamentId}|${refereeId}`;

    setPending((prev) => {
      replaced = prev.has(refereeId)
        ? { local: true, rates: prev.get(refereeId) }
        : { local: false };

      const current = replaced.local
        ? replaced.rates
        : ratesById.get(refereeId);

      return new Map(prev).set(refereeId, nextRates(current));
    });

    const count = writing.current.get(writeKey) || 0;
    writing.current.set(writeKey, count + 1);

    try {
      await write();
    } catch (error) {
      toast.error(error?.message ?? 'Failed to save the rate');
      // After a switch `pending` is another tournament's copy, not ours
      if (tournamentId === tournIdRef.current)
        setPending((prev) => {
          const rolledBack = new Map(prev);
          if (replaced.local) rolledBack.set(refereeId, replaced.rates);
          else rolledBack.delete(refereeId);
          return rolledBack;
        });
      // The caller still has the typed amount on screen and has to drop it
      throw error;
    } finally {
      const left = writing.current.get(writeKey) - 1;
      if (left > 0) writing.current.set(writeKey, left);
      else writing.current.delete(writeKey);
    }

    // The last write of this referee to land records what the snapshot says
    // now; whatever it says next is the backend's answer, kept or not
    if (!writing.current.has(writeKey) && tournamentId === tournIdRef.current)
      settled.current.set(refereeId, latestById.current.get(refereeId));
  };

  const setRefereeRate = (refereeId, rate) =>
    apply(
      refereeId,
      (rates) => ({ rate, exceptions: rates?.exceptions || {} }),
      () => tournamentRefereeRateSet({ id: tournamentId, refereeId, rate }),
    );

  // Wiping the referee's document takes their per-game rates with it
  const clearRefereeRate = (refereeId) =>
    apply(
      refereeId,
      () => null,
      () => tournamentRefereeRateSet({ id: tournamentId, refereeId }),
    );

  // The first amount entered for a referee who has none becomes their default
  // too: every game of theirs gets priced, and only the outliers need fixing.
  // Both effects land in one local update, ahead of the two writes
  const setGameRate = (refereeId, gameId, rate) => {
    const isFirst = !hasRates(getRates(refereeId));

    return apply(
      refereeId,
      (rates) => ({
        rate: isFirst ? rate : rates?.rate,
        exceptions: { ...rates?.exceptions, [gameId]: rate },
      }),
      async () => {
        if (isFirst)
          await tournamentRefereeRateSet({ id: tournamentId, refereeId, rate });
        await tournamentRefereeGameRateSet({
          id: tournamentId,
          refereeId,
          gameId,
          rate,
        });
      },
    );
  };

  const clearGameRate = (refereeId, gameId) =>
    apply(
      refereeId,
      (rates) => {
        const exceptions = { ...rates?.exceptions };
        delete exceptions[gameId];
        return { rate: rates?.rate, exceptions };
      },
      () =>
        tournamentRefereeGameRateSet({ id: tournamentId, refereeId, gameId }),
    );

  return {
    getRates,
    isRatesSynced,
    setRefereeRate,
    clearRefereeRate,
    setGameRate,
    clearGameRate,
  };
}

// What each referee has been paid for one tournament, in dollars, and the
// payouts behind it. Nothing paid is an answer only once `isPayoutsSynced` is
// up: offering the full total over a referee already paid is how one gets
// paid twice. A refusal never syncs — no Pay button beats paying twice.
export function usePayouts(tournamentId) {
  const { userManagesReferees, activeOrg, isOrgConfirmed, isOrgSwitching } =
    useFirebase();

  // No organization yet: nobody has been paid. Unordered, since only a sum
  // per referee is read; the index lives in the backend repo
  const buildQuery = useCallback(
    () =>
      activeOrg
        ? query(
            collection(db, 'payouts'),
            where('organizations', 'array-contains', activeOrg),
            where('tournamentId', '==', tournamentId),
          )
        : null,
    [activeOrg, tournamentId],
  );

  const { data, isSynced } = useLiveQuery(buildQuery, {
    // The organization behind the filter must be the confirmed one, not the
    // cached guess: a refused query here reads as "nothing paid"
    ready:
      userManagesReferees &&
      !!tournamentId &&
      isOrgConfirmed &&
      !isOrgSwitching,
    map: readTournamentPayouts,
    empty: noTournamentPayouts,
    descriptor: {
      path: 'payouts',
      filters: [
        `organizations array-contains ${activeOrg}`,
        `tournamentId == ${tournamentId}`,
      ],
      orgScoped: true,
      message: 'Failed to load referee payouts',
      toastId: 'tournament-payouts',
      extra: { tournamentId },
    },
  });

  const { paidById, payouts } = data;
  const getPaid = useCallback(
    (refereeId) => paidById.get(refereeId) || 0,
    [paidById],
  );

  return { getPaid, payouts, isPayoutsSynced: isSynced };
}

const noTournamentPayouts = { paidById: new Map(), payouts: [] };

const readTournamentPayouts = (snapshot) => {
  const payouts = readPayouts(snapshot);
  const byUser = new Map();
  for (const payout of payouts) {
    if (!byUser.has(payout.userId)) byUser.set(payout.userId, []);
    byUser.get(payout.userId).push(payout);
  }
  const paidById = new Map(
    [...byUser].map(([userId, own]) => [userId, sumPaid(own)]),
  );
  return { paidById, payouts };
};

// The signed-in referee's own payouts, newest first. The rules prove the read
// through the userId filter, which the deployed index pairs with createdAt
export function useMyPayouts() {
  const { user, userIsReferee } = useFirebase();
  const uid = userIsReferee ? user?.id : null;

  const buildQuery = useCallback(
    () =>
      uid
        ? query(
            collection(db, 'payouts'),
            where('userId', '==', uid),
            orderBy('createdAt', 'desc'),
          )
        : null,
    [uid],
  );

  const { data, isLoaded, isSynced } = useLiveQuery(buildQuery, {
    map: readPayouts,
    empty: noPayouts,
    descriptor: {
      path: 'payouts',
      filters: [`userId == ${uid}`],
      message: 'Failed to load your payouts',
    },
  });

  return { payouts: data, isLoaded, isSynced };
}

const noPayouts = [];
const readPayouts = (snapshot) =>
  snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

// One live document, open while the caller is mounted: the by-id counterpart
// of useLiveQuery. `path` is its slash-joined path, or null for none; `map`
// turns an existing snapshot into data and must be stable
function useLiveDoc(path, { map, descriptor }) {
  const { user, reportListenerError } = useFirebase();
  const callerUid = user?.id;
  const errorInfo = useRef(descriptor);
  errorInfo.current = descriptor;
  const [state, setState] = useState({ path: null });

  useEffect(() => {
    if (!path) return undefined;

    return onSnapshot(
      doc(db, path),
      // Without it the cache → server flip is silent and nothing ever syncs
      { includeMetadataChanges: true },
      (snap) =>
        setState((prev) => ({
          path,
          data: snap.exists() ? map(snap) : undefined,
          isLoaded: true,
          isSynced:
            (prev.path === path && prev.isSynced) || !snap.metadata.fromCache,
        })),
      (err) => {
        reportListenerError(err, callerUid, {
          operation: 'listen',
          path,
          ...errorInfo.current,
        });
        setState({ path, data: undefined, isLoaded: true, isSynced: false });
      },
    );
  }, [path, map, callerUid, reportListenerError]);

  // No document to read is a real answer, or a caller waiting on it spins
  if (!path) return { data: undefined, isLoaded: true, isSynced: true };

  const isCurrent = state.path === path;
  return {
    data: isCurrent ? state.data : undefined,
    isLoaded: isCurrent && state.isLoaded,
    isSynced: isCurrent && state.isSynced,
  };
}

// The signed-in referee's own rate at one tournament. The rules let them get
// that one document by id and nothing more, so no query can stand in for it
export function useMyRefereeRates(tournamentId) {
  const { user } = useFirebase();
  const { data, isSynced } = useLiveDoc(
    tournamentId && user?.id
      ? `tournaments/${tournamentId}/refereeRates/${user.id}`
      : null,
    { map: readRates, descriptor: { message: 'Failed to load your rate' } },
  );
  return { rates: data, isSynced };
}

const readRates = (snap) => ({
  rate: snap.get('rate'),
  exceptions: snap.get('exceptions') || {},
});

// An organization's public profile — name and contacts — which anyone may
// read by id, visitors included
export function useOrgProfile(org) {
  const { data, isLoaded, isSynced } = useLiveDoc(
    org ? `organizations/${org}/public/profile` : null,
    {
      map: readProfile,
      descriptor: { message: 'Failed to load the organizer contacts' },
    },
  );
  return { profile: data, isLoaded, isSynced };
}

const readProfile = (snap) => ({
  name: snap.get('name') ?? '',
  contacts: snap.get('contacts') ?? '',
});

// Paginated read of a collection: the first page stays live, the ones the
// consumer scrolls to are fetched once. `buildQuery` returns the query
// arguments — collection reference first, then filters and ordering — or null
// when there is nothing to read; its identity is the reset key, so memoize it.
export function usePagedQuery(
  buildQuery,
  { pageSize = 50, compare, descriptor } = {},
) {
  const { user, reportListenerError } = useFirebase();
  const uid = user?.id;

  // Kept in a ref so a caller writing the descriptor inline does not rebuild
  // the listener on every render
  const errorInfo = useRef(descriptor);
  errorInfo.current = descriptor;

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSynced, setIsSynced] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  const docsById = useRef(new Map());
  // Where the next page starts — the last document of the last page read, kept
  // as the snapshot `startAfter` needs rather than as data
  const cursor = useRef(null);
  const exhausted = useRef(false);
  const fetching = useRef(false);
  const paged = useRef(false);
  // Bumped on every reset, so a page still in flight cannot land in the map
  // that a newer query has already started filling
  const generation = useRef(0);

  const publish = useCallback(() => {
    const list = [...docsById.current.values()];
    if (compare) list.sort(compare);
    setItems(list);
  }, [compare]);

  const merge = useCallback((docs) => {
    for (const doc of docs)
      docsById.current.set(doc.id, { id: doc.id, ...doc.data() });
  }, []);

  useEffect(() => {
    generation.current += 1;
    docsById.current = new Map();
    cursor.current = null;
    exhausted.current = false;
    fetching.current = false;
    paged.current = false;
    setItems([]);
    setIsLoadingMore(false);
    setHasMore(false);

    const args = buildQuery();
    if (!args) {
      setIsLoading(false);
      setIsSynced(true);
      return;
    }

    setIsLoading(true);
    setIsSynced(false);

    let published = false;

    return onSnapshot(
      query(...args, limit(pageSize)),
      // Without this the cache → server transition is silent whenever the two
      // agree, and `isSynced` would never flip on a warm cache
      { includeMetadataChanges: true },
      (snapshot) => {
        setIsLoading(false);
        // "The whole first page, not a subset of it." A cached snapshot may be
        // short, so anything concluded from absence — an empty state, a choice
        // among the documents — has to wait for this.
        if (!snapshot.metadata.fromCache) setIsSynced(true);

        // A metadata-only event repeats data that is already published
        if (published && !snapshot.docChanges().length) return;
        published = true;

        // Merged, never replaced: a document pushed off the first page by a
        // newer one reports as removed, and dropping it would tear a hole
        // between the live page and the fetched ones
        merge(snapshot.docs);
        // Follows the first page until paging starts — the cached snapshot
        // arrives short and would anchor the cursor above the real boundary.
        // Frozen afterwards: later arrivals shift the window, and moving the
        // anchor would re-read what has already been paged over
        if (!paged.current) cursor.current = snapshot.docs.at(-1) ?? null;
        if (!exhausted.current) setHasMore(snapshot.docs.length === pageSize);
        publish();
      },
      (err) => {
        reportListenerError(err, uid, errorInfo.current);
        // A rejected query never fires the handler above, so both gates have to
        // open here or the consumer waits on a spinner forever
        setIsLoading(false);
        setIsSynced(true);
      },
    );
  }, [buildQuery, pageSize, merge, publish, uid, reportListenerError]);

  const loadMore = useCallback(async () => {
    if (fetching.current || exhausted.current || !cursor.current) return;

    const args = buildQuery();
    if (!args) return;

    const gen = generation.current;
    fetching.current = true;
    setIsLoadingMore(true);

    try {
      const snapshot = await getDocs(
        query(...args, startAfter(cursor.current), limit(pageSize)),
      );
      if (gen !== generation.current) return;

      paged.current = true;
      merge(snapshot.docs);
      if (snapshot.docs.length) cursor.current = snapshot.docs.at(-1);
      if (snapshot.docs.length < pageSize) {
        exhausted.current = true;
        setHasMore(false);
      }
      publish();
    } catch (err) {
      reportListenerError(err, uid, errorInfo.current);
    } finally {
      if (gen === generation.current) {
        fetching.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [buildQuery, pageSize, merge, publish, uid, reportListenerError]);

  return { items, isLoading, isSynced, isLoadingMore, hasMore, loadMore };
}

// `startDate` is stored as a YYYY-MM-DD string, so ordering it as text is
// ordering it chronologically. Module level: the comparator is a dependency of
// the publish step inside usePagedQuery.
const byStartDateDesc = (a, b) =>
  (b.startDate || '').localeCompare(a.startDate || '');

// The tournament grid on the home page. Used to render the provider's whole
// `tournaments` array, which for a visitor meant every active tournament of
// every organization — plus four subcollection listeners each. Now a page of
// its own, newest first, and the rest arrive on scroll.
export function useHomeTournaments() {
  const { user, isUserResolved, activeOrg, isOrgConfirmed, isOrgSwitching } =
    useFirebase();

  // Nothing is known about the caller yet, or their organization is still the
  // cached guess — either way the filter below would be wrong, so no query goes
  // out. A confirmed absence of an organization is an answer, not a wait: a
  // freshly registered user reads unfiltered, exactly as they did before.
  const isPending =
    !isUserResolved || (!!user && !(isOrgConfirmed && !isOrgSwitching));

  const buildQuery = useCallback(() => {
    if (isPending) return null;

    return [
      collection(db, 'tournaments'),
      where('active', '==', true),
      // A visitor sends no organization filter: the public tournament pages
      // are cross-organization by design, and the rules ask for nothing.
      ...(activeOrg
        ? [where('organizations', 'array-contains', activeOrg)]
        : []),
      orderBy('startDate', 'desc'),
    ];
  }, [isPending, activeOrg]);

  const { items, isLoading, isLoadingMore, hasMore, loadMore } = usePagedQuery(
    buildQuery,
    {
      pageSize: 12,
      compare: byStartDateDesc,
      descriptor: {
        operation: 'listen',
        path: 'tournaments',
        filters: ['active == true', 'order by startDate desc'],
        orgScoped: !!user,
        message: 'Failed to load tournaments',
      },
    },
  );

  // The grid expects the same shape the provider publishes — moments for the
  // dates, Storage URLs for the cover. A hidden event is not public: dropped
  // here, since `hidden == false` would also drop every doc without the field
  const tournaments = useMemo(
    () => items.filter((t) => t.hidden !== true).map(normalizeTournamentDoc),
    [items],
  );

  return {
    tournaments,
    // `usePagedQuery` reports a withheld query as "not loading" — there is
    // nothing in flight. For the grid it is still a wait, and on a warm start
    // it lasts until the server confirms the organization: without this the
    // section renders empty for a second or two instead of spinning.
    isLoading: isLoading || isPending,
    isLoadingMore,
    hasMore,
    loadMore,
  };
}

// Every payments query has to carry the filters the rules can prove against
// each document it would return: the organization for either role, and the
// coach's own teams on top. Null means this role reads no payments at all.
function paymentsScope({ activeOrg, role, teams }) {
  if (!activeOrg) return null;
  if (role === UsersRoles.ADMIN)
    return [where('organizations', 'array-contains', activeOrg)];
  if (role === UsersRoles.COACH && teams.length)
    return [
      where('organizations', 'array-contains', activeOrg),
      where('details.team', 'in', teams),
    ];
  return null;
}

// The organization behind the filter is only a cached guess until the server
// confirms it — a query sent on the wrong one is refused, and the refusal
// toasts at someone who did nothing wrong
function useConfirmedOrg() {
  const { activeOrg, isOrgConfirmed, isOrgSwitching } = useFirebase();
  return isOrgConfirmed && !isOrgSwitching ? activeOrg : null;
}

// Primitives instead of the `user` object: it gets a new identity on every
// self-document patch, and the query depends on none of that
function useMyTeams() {
  const { user } = useFirebase();
  const teamsKey = user?.teams?.join('|') ?? '';
  return useMemo(() => (teamsKey ? teamsKey.split('|') : []), [teamsKey]);
}

const byTimeDesc = (a, b) =>
  (b.time?.toMillis() ?? 0) - (a.time?.toMillis() ?? 0);

// One page of payments at a time for the Payments tab. Both filters go into the
// query: applied in memory they would only ever match the pages already read.
// `period` is a "MMMM YYYY" label, `method` a `PaymentMethods` value.
export function usePaymentsPage({ method, period }) {
  const { user } = useFirebase();
  const activeOrg = useConfirmedOrg();
  const teams = useMyTeams();
  const role = user?.role;

  const buildQuery = useCallback(() => {
    const scope = paymentsScope({ activeOrg, role, teams });
    if (!scope) return null;

    const args = [collection(db, 'payments'), ...scope];
    if (method) args.push(where('method', '==', method));
    if (period) {
      const start = moment(period, 'MMMM YYYY').startOf('month');
      args.push(where('time', '>=', start.toDate()));
      args.push(where('time', '<', start.clone().add(1, 'month').toDate()));
    }
    args.push(orderBy('time', 'desc'));

    return args;
  }, [activeOrg, role, teams, method, period]);

  return usePagedQuery(buildQuery, {
    pageSize: 50,
    compare: byTimeDesc,
    descriptor: {
      operation: 'listen',
      path: 'payments',
      filters: [`method ${method ?? 'any'}`, `period ${period ?? 'any'}`],
      orgScoped: true,
      message: 'Failed to load payments',
    },
  });
}

// Sums of tournament payments, read whole because one tournament's payments are
// bounded by the number of teams in it. Empty map while the argument is missing
// or the role may not read them — no query goes out then.
function usePaymentSums(scope, groupBy) {
  const buildQuery = useCallback(
    () => scope && query(collection(db, 'payments'), ...scope),
    [scope],
  );
  const map = useMemo(() => readPaymentSums(groupBy), [groupBy]);

  const { data } = useLiveQuery(buildQuery, {
    map,
    empty: noSums,
    descriptor: {
      path: 'payments',
      orgScoped: true,
      message: 'Failed to load payments',
      toastId: 'payment-sums',
    },
  });

  return data;
}

const noSums = new Map();
const readPaymentSums = (groupBy) => (snapshot) => {
  const sums = new Map();
  for (const doc of snapshot.docs) {
    const { purpose, details, amount } = doc.data();
    if (purpose !== PaymentPurposes.TOURNAMENT) continue;
    const key = details?.[groupBy];
    if (!key) continue;
    sums.set(key, (sums.get(key) ?? 0) + amount);
  }
  return sums;
};

// What each team has paid for one tournament, keyed by team id
export function useTournamentPayments(tournamentId) {
  const { user } = useFirebase();
  const activeOrg = useConfirmedOrg();
  const teams = useMyTeams();
  const role = user?.role;

  const scope = useMemo(() => {
    if (!tournamentId) return null;
    const base = paymentsScope({ activeOrg, role, teams });
    return base && [...base, where('details.tournament', '==', tournamentId)];
  }, [activeOrg, role, teams, tournamentId]);

  return usePaymentSums(scope, 'team');
}

// What one team has paid, keyed by tournament id. Only an admin and the team's
// own coach may ask: a player carries `teams` just the same, and the rules
// answer to the role. `details.team ==` is what proves a coach's read
export function useTeamPayments(teamId) {
  const { userIsAdmin, userIsCoach } = useFirebase();
  const activeOrg = useConfirmedOrg();
  const teams = useMyTeams();

  const scope = useMemo(() => {
    if (!activeOrg || !teamId) return null;
    if (!userIsAdmin && !(userIsCoach && teams.includes(teamId))) return null;
    return [
      where('organizations', 'array-contains', activeOrg),
      where('details.team', '==', teamId),
    ];
  }, [activeOrg, userIsAdmin, userIsCoach, teams, teamId]);

  return usePaymentSums(scope, 'tournament');
}

const byCreatedAtAsc = (a, b) =>
  (a.createdAt?.toMillis() ?? 0) - (b.createdAt?.toMillis() ?? 0);

// One chat's history: the newest page stays live and older ones arrive as the
// thread is scrolled up. Read newest-first and ordered ascending in memory —
// the direction of the query only decides which end is read first, and a chat
// is read from the bottom. Served by the automatic single-field index.
//
// A null `chatId` reads nothing, which is how a closed window stands the
// listener down (see ChatThread).
export function useChatMessages(chatId) {
  const buildQuery = useCallback(() => {
    if (!chatId) return null;
    return [
      collection(db, 'chats', chatId, 'messages'),
      orderBy('createdAt', 'desc'),
    ];
  }, [chatId]);

  return usePagedQuery(buildQuery, {
    pageSize: 20,
    compare: byCreatedAtAsc,
    descriptor: {
      operation: 'listen',
      path: chatId ? `chats/${chatId}/messages` : 'chats/messages',
      filters: ['orderBy createdAt desc'],
      message: 'Failed to load messages',
    },
  });
}
